import { useMutation, useQuery } from "@apollo/client";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  InputBase,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { downloadCsv, localDate, toCsv } from "./csv";
import { REPORT_DAILY, SET_DAILY_REPORT_TARGET, SET_DAILY_REPORT_VALUE } from "./dashboardReports.queries";
import { DateRangeValue } from "./DateRangeField";
import { browserTimezone, formatCount } from "./reportFormat";
import { ReportError } from "./reportParts";
import { DAILY_PDF_HEADINGS, DailyRow, DailySection as Section } from "./pdf/dailyReportPdf";
import { DailyNoteField, DailyReportPdfButton } from "./pdf/exportButtons";

/** Titled as the team's report titles its sections, so the page and the PDF read the same. */
const SECTIONS: Record<Section, { title: string; note: string }> = {
  CALL: { title: DAILY_PDF_HEADINGS.CALL, note: "Typed in after the call. Click a cell to enter a number." },
  LEVELS: { title: DAILY_PDF_HEADINGS.LEVELS, note: "Members who reached each level that day, and how many are on it now." },
  GROWTH: {
    title: DAILY_PDF_HEADINGS.GROWTH,
    note: "Accounts and members come from the app; downloads and lead generation are typed in.",
  },
  ACTIVITY: { title: DAILY_PDF_HEADINGS.ACTIVITY, note: "All worked out from the app; chat messages are counted by getstream." },
};

/** Column widths, the same in every section so each day sits in one column down the page. */
const METRIC_WIDTH = 300;
const TARGET_WIDTH = 104;
const DAY_WIDTH = 84;
const TOTAL_WIDTH = 96;

/** Status "good" from the reference palette; always shown with its icon, never alone. */
const MET = "#0ca30c";

const dayHeading = (day: string) => {
  const date = new Date(`${day}T00:00:00Z`);
  return {
    weekday: date.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }),
    date: date.toLocaleDateString("en-US", { day: "numeric", month: "short", timeZone: "UTC" }),
  };
};

/**
 * A number an admin can change in place: click, type, Enter or click away to
 * save, Escape to cancel. Emptying it clears the value.
 */
const EditableNumber: React.FC<{
  value: number | null;
  onSave: (next: number | null) => Promise<unknown>;
  label: string;
  children: React.ReactNode;
}> = ({ value, onSave, label, children }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    if (draft === null) return;
    const text = draft.trim().replace(/,/g, "");
    const next = text === "" ? null : Number(text);
    if (next !== null && (!Number.isInteger(next) || next < 0)) {
      toast.error("Enter a whole number, or leave it empty to clear it.");
      return;
    }
    if (next === value) {
      setDraft(null);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setDraft(null);
    } catch (err: any) {
      toast.error(err?.message ?? "Could not save that number.");
    } finally {
      setSaving(false);
    }
  };

  if (draft !== null) {
    return (
      <InputBase
        autoFocus
        value={draft}
        disabled={saving}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
          if (event.key === "Escape") setDraft(null);
        }}
        inputProps={{ inputMode: "numeric", "aria-label": label, style: { textAlign: "right", padding: 0 } }}
        sx={(theme) => ({
          width: "100%",
          minWidth: 56,
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          border: `1px solid ${theme.palette.primary.main}`,
          bgcolor: "background.paper",
          fontVariantNumeric: "tabular-nums",
        })}
      />
    );
  }
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Edit ${label}`}
      onClick={() => setDraft(value === null ? "" : String(value))}
      onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && setDraft(value === null ? "" : String(value))}
      sx={(theme) => ({
        cursor: "text",
        borderRadius: 1,
        px: 0.75,
        py: 0.25,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 0.5,
        border: "1px dashed transparent",
        "& .edit-hint": { opacity: 0 },
        "&:hover, &:focus-visible": { borderColor: theme.palette.divider, bgcolor: alpha(theme.palette.primary.main, 0.04) },
        "&:hover .edit-hint, &:focus-visible .edit-hint": { opacity: 1 },
      })}
    >
      <EditOutlined className="edit-hint" sx={{ fontSize: 14, color: "text.secondary" }} />
      {children}
    </Box>
  );
};

/** A day's number, with a tick when it meets the row's target. */
const Value: React.FC<{ value: number | null; target: number | null }> = ({ value, target }) => {
  if (value === null) {
    return <Box sx={{ color: "text.disabled" }}>—</Box>;
  }
  const met = target !== null && target > 0 && value >= target;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: value === 0 ? "text.disabled" : "text.primary" }}>
      {met && (
        <Tooltip title={`Met the daily target of ${formatCount(target)}`}>
          <CheckCircleRounded sx={{ fontSize: 15, color: MET }} aria-label="Target met" />
        </Tooltip>
      )}
      {formatCount(value)}
    </Box>
  );
};

const SectionTable: React.FC<{
  section: Section;
  rows: DailyRow[];
  days: string[];
  onSaveValue: (metric: string, day: string, value: number | null) => Promise<unknown>;
  onSaveTarget: (metric: string, value: number | null) => Promise<unknown>;
  /** The written note under the heading, as in the team's report. */
  note: React.ReactNode;
}> = ({ section, rows, days, onSaveValue, onSaveTarget, note }) => {
  const showSoFar = section === "LEVELS";
  const columns = (
    <colgroup>
      <col style={{ width: METRIC_WIDTH }} />
      <col style={{ width: TARGET_WIDTH }} />
      {days.map((day) => (
        <col key={day} style={{ width: DAY_WIDTH }} />
      ))}
      <col style={{ width: TOTAL_WIDTH }} />
      <col style={{ width: TOTAL_WIDTH }} />
    </colgroup>
  );
  const cell = { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", py: 1 } as const;
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Typography variant="h6">{SECTIONS[section].title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {SECTIONS[section].note}
        </Typography>
        <Box sx={{ mt: 1.5, maxWidth: 720 }}>{note}</Box>
      </Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
            minWidth: METRIC_WIDTH + TARGET_WIDTH + days.length * DAY_WIDTH + 2 * TOTAL_WIDTH,
            "& td, & th": { borderColor: "divider" },
          }}
        >
          {columns}
          <TableHead>
            <TableRow sx={{ "& th": { bgcolor: "action.hover", color: "text.secondary", fontWeight: 600, fontSize: 12, py: 1 } }}>
              {/* Sticky, so it must be opaque: the header tint laid over the paper colour. */}
              <TableCell
                sx={(theme) => ({
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                  background: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover}), ${theme.palette.background.paper} !important`,
                })}
              >
                Metric
              </TableCell>
              <TableCell align="right">Daily target</TableCell>
              {days.map((day) => {
                const { weekday, date } = dayHeading(day);
                return (
                  <TableCell key={day} align="right">
                    <Box sx={{ fontWeight: 400 }}>{weekday}</Box>
                    {date}
                  </TableCell>
                );
              })}
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">{showSoFar ? "On it now" : ""}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.metric} hover>
                <TableCell sx={{ position: "sticky", left: 0, zIndex: 1, bgcolor: "background.paper", py: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ fontWeight: 500 }}>{row.label}</Box>
                    {row.source === "MANUAL" && <Chip label="Typed in" size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {row.hint}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ ...cell, color: "text.secondary" }}>
                  <EditableNumber value={row.target} label={`daily target for ${row.label}`} onSave={(value) => onSaveTarget(row.metric, value)}>
                    {row.target === null ? <Box sx={{ color: "text.disabled" }}>Set</Box> : formatCount(row.target)}
                  </EditableNumber>
                </TableCell>
                {row.values.map((value, index) => (
                  <TableCell key={days[index]} align="right" sx={cell}>
                    {row.source === "MANUAL" ? (
                      <EditableNumber
                        value={value}
                        label={`${row.label} on ${days[index]}`}
                        onSave={(next) => onSaveValue(row.metric, days[index], next)}
                      >
                        <Value value={value} target={row.target} />
                      </EditableNumber>
                    ) : (
                      <Box sx={{ px: 0.75 }}>
                        <Value value={value} target={row.target} />
                      </Box>
                    )}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ ...cell, fontWeight: 700 }}>
                  {row.total === null ? <Box sx={{ color: "text.disabled" }}>—</Box> : formatCount(row.total)}
                </TableCell>
                <TableCell align="right" sx={{ ...cell, fontWeight: 700, bgcolor: showSoFar ? "action.hover" : undefined }}>
                  {showSoFar ? formatCount(row.totalSoFar) : ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};

/**
 * The team's daily report, generated: one table per section, a column per day.
 * Rows the app can work out are filled in; the rest (Zoom, Facebook, app store
 * downloads, lead generation) are typed in here and kept. See the backend's
 * docs/DASHBOARD_REPORTS.md, "Daily report". The weekly and monthly reports are
 * these same tables over a closed period.
 */
export const DailyReportTab: React.FC<{ dates: DateRangeValue; period?: "daily" | "weekly" | "monthly" }> = ({ dates, period = "daily" }) => {
  const variables = {
    range: { dateFrom: dates.from!.toISOString(), dateTo: dates.to!.toISOString(), timezone: browserTimezone() },
  };
  const { data, loading, error, refetch } = useQuery(REPORT_DAILY, { variables, fetchPolicy: "cache-and-network" });
  const [setValue] = useMutation(SET_DAILY_REPORT_VALUE);
  const [setTarget] = useMutation(SET_DAILY_REPORT_TARGET);

  const report = data?.reportDaily;
  const days: string[] = report?.days ?? [];
  const rows: DailyRow[] = report?.rows ?? [];
  const sections = (["CALL", "LEVELS", "GROWTH", "ACTIVITY"] as Section[]).filter((section) => rows.some((row) => row.section === section));

  const saveValue = async (metric: string, day: string, value: number | null) => {
    await setValue({ variables: { day, metric, value } });
    await refetch();
  };
  const saveTarget = async (metric: string, value: number | null) => {
    await setTarget({ variables: { metric, value } });
    await refetch();
  };

  const exportCsv = () => {
    const header = ["Section", "Metric", "Source", "Daily target", ...days, "Total", "On it now"];
    const lines = rows.map((row) => [
      SECTIONS[row.section].title,
      row.label,
      row.source === "MANUAL" ? "Typed in" : "App",
      row.target,
      ...row.values,
      row.total,
      row.totalSoFar,
    ]);
    downloadCsv(`${period}-report-${days[0] ?? localDate(new Date())}-to-${days[days.length - 1] ?? ""}.csv`, toCsv([header, ...lines]));
  };

  const earliest = days[0];
  const levelsGap = report && (!report.levelsRecordedSince || (earliest && earliest < report.levelsRecordedSince));

  return (
    <Stack spacing={2.5}>
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Button variant="contained" startIcon={<FileDownloadOutlined />} disabled={!report || loading} onClick={exportCsv}>
          Export CSV
        </Button>
        {/* That PDF is the team's daily report, headed with one day's date. */}
        {period === "daily" && <DailyReportPdfButton report={report && !loading ? report : null} onNotesSaved={refetch} />}
      </Box>

      <ReportError error={error} />
      {loading && <LinearProgress />}

      {levelsGap && (
        <Alert severity="info" variant="outlined">
          Level-ups are recorded from{" "}
          {report.levelsRecordedSince ? <b>{report.levelsRecordedSince}</b> : "the first one after this update"}. Days before
          that show 0 because nothing was recorded then, not because nobody levelled up.
        </Alert>
      )}

      {report?.chatMessagesSource === "APP" && (
        <Alert severity="warning" variant="outlined">
          getstream could not be read just now, so <b>Sent a message in the Chat group</b> comes from the app's own log, which misses
          messages sent from older app builds.
        </Alert>
      )}
      {report && earliest && earliest < report.phoneVerifiedDatesExactSince && (
        <Alert severity="info" variant="outlined">
          Phone verifications are recorded from <b>{report.phoneVerifiedDatesExactSince}</b>. Before that, <b>Members (name, phone number,
          email)</b> counts a member on the day they registered.
        </Alert>
      )}

      {sections.map((section) => (
        <SectionTable
          key={section}
          section={section}
          days={days}
          rows={rows.filter((row) => row.section === section)}
          onSaveValue={saveValue}
          onSaveTarget={saveTarget}
          note={
            // A note belongs to one day's report, so a week's would overwrite its last day's.
            period === "daily" && (
              <DailyNoteField
                day={days[days.length - 1]}
                section={section}
                saved={report.notes.find((item: { section: Section }) => item.section === section)?.text ?? ""}
                onSaved={refetch}
                label="Note"
              />
            )
          }
        />
      ))}
    </Stack>
  );
};
